/**
 * Permission Utility Functions
 * Simple permission checking based on specific permission strings
 * All permissions are stored in Redux store only - NO localStorage usage
 */

/**
 * Check if user has a specific permission
 * @param {Array} permissions - Array of permission strings from Redux store
 * @param {string} permission - Permission string to check (e.g., 'vendor/view')
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (permissions, permission) => {
  if (!Array.isArray(permissions) || !permission) {
    return false;
  }
  return permissions.includes(permission);
};

/**
 * Permission constants for easy reference
 */
export const PERMISSIONS = {
  // Vendor permissions
  VENDOR_VIEW: 'vendor/view',
  VENDOR_CREATE: 'vendor/create',
  VENDOR_EDIT: 'vendor/edit',
  VENDOR_DELETE: 'vendor/delete',
  
  // Product permissions
  PRODUCT_VIEW: 'product/view',
  PRODUCT_CREATE: 'product/create',
  PRODUCT_EDIT: 'product/edit',
  PRODUCT_DELETE: 'product/delete',
  
  // Purchase Order permissions
  PURCHASE_ORDER_VIEW: 'purchase_order/view',
  PURCHASE_ORDER_CREATE: 'purchase_order/create',
  PURCHASE_ORDER_EDIT: 'purchase_order/edit',
  PURCHASE_ORDER_DELETE: 'purchase_order/delete',
  // Allows increasing a PO line item's ordered quantity to match an
  // over-delivery (vendor supplied more than was ordered).
  PURCHASE_ORDER_AMEND: 'purchase_order/amend',
  
  // Delivery Address permissions
  DELIVERY_ADDRESS_VIEW: 'delivery_address/view',
  DELIVERY_ADDRESS_CREATE: 'delivery_address/create',
  DELIVERY_ADDRESS_EDIT: 'delivery_address/edit',
  DELIVERY_ADDRESS_DELETE: 'delivery_address/delete',
  
  // Category permissions
  CATEGORY_VIEW: 'category/view',
  CATEGORY_CREATE: 'category/create',
  CATEGORY_EDIT: 'category/edit',
  CATEGORY_DELETE: 'category/delete',
  
  // Product Category permissions
  PRODUCT_CATEGORY_VIEW: 'product_category/view',
  PRODUCT_CATEGORY_CREATE: 'product_category/create',
  PRODUCT_CATEGORY_EDIT: 'product_category/edit',
  PRODUCT_CATEGORY_DELETE: 'product_category/delete',
  
  // Udhyam Category permissions
  UDHYAM_CATEGORY_VIEW: 'udhyam_category/view',
  UDHYAM_CATEGORY_CREATE: 'udhyam_category/create',
  UDHYAM_CATEGORY_EDIT: 'udhyam_category/edit',
  UDHYAM_CATEGORY_DELETE: 'udhyam_category/delete',
  
  // Firm Name permissions
  FIRM_NAME_VIEW: 'firm_name/view',
  FIRM_NAME_CREATE: 'firm_name/create',
  FIRM_NAME_EDIT: 'firm_name/edit',
  FIRM_NAME_DELETE: 'firm_name/delete',
  
  // Order permissions
  ORDER_VIEW: 'order/view',
  ORDER_CREATE: 'order/create',
  ORDER_EDIT: 'order/edit',
  ORDER_DELETE: 'order/delete',
  
  // Config permissions
  CONFIG_VIEW: 'config/view',
  CONFIG_CREATE: 'config/create',
  CONFIG_EDIT: 'config/edit',
  CONFIG_DELETE: 'config/delete',
  
  // Audit permissions
  AUDIT_VIEW: 'audit/view',
  AUDIT_CREATE: 'audit/create',
  AUDIT_EDIT: 'audit/edit',
  AUDIT_DELETE: 'audit/delete',
  AUDIT_EXPORT: 'audit/export',
  
  // User permissions
  USER_VIEW: 'user/view',
  USER_CREATE: 'user/create',
  USER_EDIT: 'user/edit',
  USER_DELETE: 'user/delete',
  USER_CHANGE_ROLE: 'user/change_role',
  
  // Role permissions
  ROLE_VIEW: 'role/view',
  ROLE_CREATE: 'role/create',
  ROLE_EDIT: 'role/edit',
  ROLE_DELETE: 'role/delete',
  ROLE_MANAGE_PERMISSIONS: 'role/manage_permissions',
  
  // Dashboard permissions
  DASHBOARD_VIEW: 'dashboard/view',
  DASHBOARD_VIEW_ANALYTICS: 'dashboard/view_analytics'
};

/**
 * Resource name mapping for menu items
 */
export const RESOURCE_MAP = {
  '/purchase-order': 'purchase_order',
  '/products': 'product',
  '/vendors': 'vendor',
  '/audit-logs': 'audit',
  '/site-configurations': 'config',
  '/delivery-addresses': 'delivery_address',
  '/product-categories': 'product_category',
  '/udhyam-categories': 'udhyam_category',
  '/firm-names': 'firm_name',
  '/dashboard': 'dashboard',
  '/users': 'user',
  '/roles': 'role'
};
